import { type Auth0ContextInterface, type OAuthError } from '@auth0/auth0-react';
import { type SeverityLevel } from '@sentry/core';
import { type QueryClient, queryOptions } from '@tanstack/react-query';
import { type AnyRouter, redirect } from '@tanstack/react-router';
import isNetworkError from 'is-network-error';
import { setSentryAuthSubject } from '@/helpers/errors/identity';
import { reportBreadcrumb, reportError } from '@/helpers/errors/report';
import { AUTH_REDIRECT_URL, buildReturnUrl } from '@/helpers/url';
import { type LogoutSearchSchema } from '@/router/helpers/auth-schemas';
import { type Claims } from '@/utils/user-utils';
import { type OAuthAccessDeniedCodeType } from '../error-codes';
import { authQueryKey } from '../query-keys';

export type AuthDto = { expiresIn: number; sessionToken: string; claims: Claims };

const EXPIRES_LEEWAY_MS = 10_000;

/**
 * Securely refresh the access token using Auth0's SDK.
 *
 * This is a critical part of the security of the application. We follow the best practices for SPAs:
 *
 * - we allow the access token lifetime by a couple of minutes only (1-5 mins)
 * - we use rotating refresh tokens with a total of 7 days lifetime, and 2.5 days of inactive lifetime
 *
 * The call `auth.getAccessTokenSilently` refreshes the token automatically to obtain a new access token.
 * We issue this call if expired before any other request, by preflighting any API requests with our interceptors,
 * and storing it in memory for the responses' `expires_in` - 10 seconds. Only once a request hits that cache-miss it preflights again.
 * We can guarantee that API requests that need a non-expired, fresh access token will always have a new one available to set its `Authorization` header.
 *
 * @see https://auth0.com/docs/secure/tokens/refresh-tokens/refresh-token-rotation#maintain-user-sessions-in-spas
 */

export const getAuthRefreshTokenQueryOptions = (
  auth: Omit<Auth0ContextInterface, 'isLoading' | 'user'>,
  router: AnyRouter,
) =>
  queryOptions<AuthDto>({
    queryKey: authQueryKey,
    queryFn: async () => {
      try {
        const response = await auth.getAccessTokenSilently({
          cacheMode: 'on',
          authorizationParams: {
            redirect_uri: AUTH_REDIRECT_URL,
            scope: window.__QDRANT_CLOUD__.auth0.scope,
            audience: window.__QDRANT_CLOUD__.auth0.audience,
          },
          detailedResponse: true,
          timeoutInSeconds: 30,
        });

        const claims = await auth.getIdTokenClaims();
        if (!claims) {
          throw new Error('Failed to retrieve ID token claims.');
        }

        // `IdToken` declares no `sub`, only an `any` index signature, so narrow it before use.
        if (typeof claims.sub === 'string') {
          setSentryAuthSubject(claims.sub);
        }

        return { expiresIn: response.expires_in * 1_000, sessionToken: response.access_token, claims };
      } catch (err) {
        const search: LogoutSearchSchema = {
          aerr: 'unknown',
          return_to: buildReturnUrl(router),
        };
        if (isOAuthError(err)) {
          // See https://community.auth0.com/t/troubleshoot-refresh-token-unknown-or-invalid-before-expiry-403-unknown-or-invalid-refresh-token/120406#p-305291-cause-3
          switch (err.error) {
            // See https://community.auth0.com/t/getaccesstokensilently-throws-error-login-required/52333/4
            case 'login_required':
            case 'invalid_token':
              search.aerr = 'invalid';
              break;
            case 'invalid_grant': // <-- refresh token is invalid or expired
            case 'missing_refresh_token': // <-- refresh token is missing, e.g.: due to a logout in another tab
              search.aerr = 'expired';
              break;
            /**
             * Thrown when network requests to the Auth server timeout.
             * @see https://github.com/auth0/auth0-spa-js/blob/f2e566849efa398ca599daf9ebdfbbd62fcb1894/src/http.ts#L153
             */
            case 'timeout':
            case 'request_error':
              search.aerr = 'internal';
              onTokenAccessError(err, 'warning');
              break;
            default:
              if (isOAuthAccessDeniedError(err)) {
                search.aerr = err.error_description;
              }
              onTokenAccessError(err, 'fatal');
          }
        } else {
          /**
           * `TimeourError` can happen if acquiring a lock between tabs takes too long, see:
           * https://github.com/auth0/auth0-spa-js/blob/fbe1344/src/Auth0Client.ts#L689
           * https://github.com/auth0/auth0-spa-js/blob/fbe1344/src/Auth0Client.ts#L897C50-L897C75
           *
           * Acquiring a lock between tabs can take up 50 secs before throwing, so we
           * set `authorizeTimeoutInSeconds` to 10 secs to minimize the chances of this happening.
           *
           * The error may also happen if a message posted from their iframe takes too long, see:
           * https://github.com/auth0/auth0-spa-js/blob/main/src/utils.ts#L58
           */
          if (isNetworkError(err) || isGenericTimeoutError(err)) {
            // Rethrown for the retry below. A network error that survives the retry is then dropped
            // by `isRetryableError` at the QueryClient choke point, so this breadcrumb is the only
            // record it ever leaves.
            reportBreadcrumb('Access token refresh could not reach Auth0', {
              domain: 'auth',
              level: 'warning',
              data: { reason: isNetworkError(err) ? 'network' : 'lock-timeout' },
            });

            throw err;
          }
          onTokenAccessError(err, 'fatal');
        }
        // Every branch above ends in a forced logout, and most of them deliberately raise no event:
        // a rotated-out or cross-tab-invalidated refresh token is an expected way for a session to
        // end, not a defect. Recording the reason here keeps that decision from also making the
        // logout invisible when a later error needs explaining.
        reportBreadcrumb('Forced logout: access token refresh failed', {
          domain: 'auth',
          level: 'warning',
          data: { aerr: search.aerr, oauthError: isOAuthError(err) ? err.error : undefined },
        });

        // Finally, throw to redirect to logout after handling edge cases above...
        throw redirect({ to: '/logout', search });
      }
    },
    retry: defaultMutationRetry,
    /**
     * We want to mark the query as stale after the expiresIn time has passed.
     *
     * We provide a leeway of REQUEST_TIMEOUT_SECONDS to ensure the token is refreshed before it expires,
     * given that internals of the queryClient may be taking some time to set `updatedAt` and the
     * delta could e off by a few milliseconds.
     */
    staleTime: (query) =>
      query.state.data?.expiresIn ? Math.max(query.state.data.expiresIn - EXPIRES_LEEWAY_MS, 0) : 0,
    /**
     * The QueryClient choke point derives `domain` from the Connect query key, which this query has
     * no equivalent of, and falls back to the current route's. Without this, a refresh failure that
     * reaches that handler (an Auth0 lock timeout, say) is filed under whatever screen the user
     * happened to be on rather than under auth.
     */
    meta: {
      sentryContext: {
        tags: { domain: 'auth', action: 'get-access-token-silently' },
      },
    },
  });

/**
 * How fresh the access token was at the moment a request failed, for attaching to the report of that
 * failure. Read from the auth query's cache entry, whose `dataUpdatedAt` is when the token was
 * issued and whose `expiresIn` is the lifetime Auth0 granted it.
 *
 * A `Unauthenticated` response on a token this says was still valid is the one piece of evidence
 * that tells apart a leeway that is too small, a clock skew, and a backend rejecting a good token.
 */
export function getTokenFreshness(queryClient: QueryClient) {
  const state = queryClient.getQueryState<AuthDto>(authQueryKey);
  if (!state?.data || !state.dataUpdatedAt) {
    return undefined;
  }

  const tokenAgeMs = Date.now() - state.dataUpdatedAt;

  return {
    tokenAgeMs,
    // Negative means the token was already expired when the request went out.
    tokenRemainingMs: state.data.expiresIn - tokenAgeMs,
    tokenLeewayMs: EXPIRES_LEEWAY_MS,
  };
}

/**
 * getAccessTokenSilently() internally retries 3 times {@link https://github.com/auth0/auth0-spa-js/blob/f2e566849efa398ca599daf9ebdfbbd62fcb1894/src/http.ts#L110}
 * except for network errors, which we will allow a retry once for.
 * */
function defaultMutationRetry(failureCount: number, error: unknown) {
  if (isNetworkError(error) || isGenericTimeoutError(error)) {
    return failureCount < 1;
  }
  return false;
}

/**
 * Generic error TimeoutError from the Auth0 SDK.
 * @see https://github.com/auth0/auth0-spa-js/blob/main/src/errors.ts#L42
 */
function isGenericTimeoutError(error: unknown) {
  return String(error).toLowerCase().includes('timeout');
}

/**
 * Check if the error is an access_denied error from Auth0.
 */
function isOAuthAccessDeniedError(error: OAuthError): error is OAuthError & {
  error: 'access_denied';
  error_description: OAuthAccessDeniedCodeType;
} {
  return error.error === 'access_denied';
}

// Something we didn't account for happened, send to Sentry and proceed.
function onTokenAccessError(error: unknown, level: SeverityLevel = 'error') {
  const oauthError = isOAuthError(error) ? error.error : undefined;
  // `access_denied` is the single OAuth code covering every reason the Auth0 post-login actions deny
  // a token (see apps/auth-provider), so the description is what actually names the failure.
  const reason = isOAuthError(error) && isOAuthAccessDeniedError(error) ? error.error_description : undefined;

  reportError(error, {
    action: 'get-access-token-silently',
    domain: 'auth',
    extra: { route: window.location.toString() },
    /**
     * Every failure here is thrown from the same line of the same function, so Sentry's stack-based
     * grouping would file an expired refresh token, an Auth0 outage and an MFA denial as one issue.
     * Group by what failed instead.
     */
    fingerprint: ['get-access-token-silently', oauthError ?? 'unknown', reason ?? ''],
    level,
    tags: { 'oauth.error': oauthError, 'oauth.reason': reason },
  });
}

// See: https://github.com/auth0/auth0-react/blob/main/src/utils.tsx#L18
function isOAuthError(err: unknown): err is OAuthError {
  return err != null && typeof err === 'object' && 'error' in err && typeof err.error === 'string';
}
